<mat-vertical-stepper [linear]="isLinear" #stepper *ngIf="(isExtraSmall$ | async)?.matches; else horizontalStep">
  <!-- Step 1: Measure Type -->
  <mat-step [stepControl]="firstFormGroup" label="Measure type">
    <app-measure-type [measureForm]="firstFormGroup"></app-measure-type>
    <div class="wizard-button-action">
      <button mat-button matStepperNext>Next</button>
    </div>
  </mat-step>

  <!-- Step 2: Measure Frequency -->
  <mat-step [stepControl]="secondFormGroup" label="Measure frequency">
    <app-measure-frequency [measureFrequencyForm]="secondFormGroup" [disableFixedValue]="false"></app-measure-frequency>
    <div class="wizard-button-action">
      <button mat-button matStepperPrevious>Back</button>
      <button mat-button matStepperNext (click)="onComparitorEnter()">Next</button>
    </div>
  </mat-step>

  <!-- Step 3: Thresholds -->
  <mat-step label="Target">
    <ng-container *ngTemplateOutlet="thresholdStep"></ng-container>
    <div class="wizard-button-action">
      <button mat-button matStepperPrevious>Back</button>
      <button mat-button matStepperNext [disabled]="!validateThresholds()">Next</button>
    </div>
  </mat-step>

  <!-- Step 4: Notifications -->
  <mat-step label="Notifications">
    <ng-container *ngTemplateOutlet="notificationStep"></ng-container>
    <div class="wizard-button-action">
      <button mat-button matStepperPrevious>Back</button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="firstFormGroup?.invalid || secondFormGroup?.invalid || !validateThresholds() || saving"
        (click)="save()"
      >
        Save
      </button>
    </div>
  </mat-step>
</mat-vertical-stepper>

<ng-template #horizontalStep>
  <mat-horizontal-stepper [linear]="isLinear" #stepper>
    <!-- Step 1: Measure Type -->
    <mat-step [stepControl]="firstFormGroup" label="Measure type">
      <app-measure-type [measureForm]="firstFormGroup"></app-measure-type>
      <div class="wizard-button-action">
        <button mat-button matStepperNext>Next</button>
      </div>
    </mat-step>

    <!-- Step 2: Measure Frequency -->
    <mat-step [stepControl]="secondFormGroup" label="Measure frequency">
      <app-measure-frequency
        [measureFrequencyForm]="secondFormGroup"
        [disableFixedValue]="false"
      ></app-measure-frequency>
      <div class="wizard-button-action">
        <button mat-button matStepperPrevious>Back</button>
        <button mat-button matStepperNext (click)="onComparitorEnter()">Next</button>
      </div>
    </mat-step>

    <!-- Step 3: Thresholds -->
    <mat-step label="Target">
      <ng-container *ngTemplateOutlet="thresholdStep"></ng-container>
      <div class="wizard-button-action">
        <button mat-button matStepperPrevious>Back</button>
        <button mat-button matStepperNext [disabled]="!validateThresholds()">Next</button>
      </div>
    </mat-step>

    <!-- Step 4: Notifications -->
    <mat-step label="Notifications">
      <ng-container *ngTemplateOutlet="notificationStep"></ng-container>
      <div class="wizard-button-action">
        <button mat-button matStepperPrevious>Back</button>
        <button
          mat-raised-button
          color="primary"
          [disabled]="firstFormGroup?.invalid || secondFormGroup?.invalid || !validateThresholds() || saving"
          (click)="save()"
        >
          Save
        </button>
      </div>
    </mat-step>
  </mat-horizontal-stepper>
</ng-template>

<!-- Threshold step content -->
<ng-template #thresholdStep>
  <!-- Fixed: show sliders -->
  <div *ngIf="isFixed" class="threshold-sliders">
    <h2>Set threshold values for each tier</h2>
    <app-multi-thumb-slider
      *ngFor="let row of sliderRows"
      [label]="row.label"
      [min]="row.min"
      [max]="row.max"
      [step]="row.step"
      [values]="row.values"
      [mode]="row.mode"
      (valuesChange)="onSliderChange(row, $event)"
    >
    </app-multi-thumb-slider>
    <div class="tier-legend" fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="16px">
      <span class="legend-item tier-3">Tier 3 - High Risk</span>
      <span class="legend-item tier-2">Tier 2 - Medium Risk</span>
      <span class="legend-item tier-1">Tier 1 - Low Risk</span>
    </div>
  </div>

  <!-- Days/Measures: show editable card rows -->
  <div *ngIf="!isFixed" class="threshold-cards">
    <h2>Set threshold values per tier</h2>
    <div
      *ngIf="firstFormGroup.get('type').value === bloodPressureType"
      class="bp-secondary-row"
      [formGroup]="firstFormGroup"
    >
      <label>Apply rule to</label>
      <mat-radio-group class="wizard-radio-group" aria-label="Select an option" formControlName="secondary">
        <mat-radio-button *ngFor="let bpFlag of vitalFlagBPSecondary" [value]="bpFlag">{{ bpFlag }}</mat-radio-button>
      </mat-radio-group>
    </div>
    <div class="tier-card-header">
      <span class="tier-card-header-item" style="width: 120px">Tier<br><small>3 = High, 2 = Medium, 1 = Low</small></span>
      <span class="tier-card-header-item" style="width: 120px">Offset type<br><small>Direction of change</small></span>
      <span class="tier-card-header-item" style="width: 120px">Offset Amount<br><small>Threshold value to trigger the alert</small></span>
      <span class="tier-card-header-item operator-select" style="width: 200px">Operator<br><small>How the measure change compares to the offset amount</small></span>
    </div>
    <div *ngFor="let tierRow of editableTierRows; let i = index" class="tier-card">
      <mat-form-field class="tier-card-field" >
        <mat-label>Tier</mat-label>
        <mat-select [value]="tierRow.tier" (selectionChange)="onTierChange(tierRow, $event.value)">
          <mat-option *ngFor="let opt of tierOptions" [value]="opt.tier">
            {{ opt.shortLabel }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field class="tier-card-field" >
        <mat-label>Offset type</mat-label>
        <mat-select [(ngModel)]="tierRow.offset_type">
          <mat-option value="+">Add (+)</mat-option>
          <mat-option value="-">Subtract (-)</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field class="tier-card-field" >
        <mat-label>Offset Amount</mat-label>
        <input matInput type="number" [(ngModel)]="tierRow.offset_amount" min="0" />
      </mat-form-field>
      <mat-form-field class="tier-card-field operator-select" >
        <mat-label>Operator</mat-label>
        <mat-select [(ngModel)]="tierRow.operator">
          <mat-option *ngFor="let op of operatorOptions" [value]="op.value">
            {{ op.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-icon-button color="warn" (click)="removeTierRow(i)" [disabled]="editableTierRows.length <= 1">
        <mat-icon>delete</mat-icon>
      </button>
    </div>
    <button mat-flat-button color="primary" (click)="addTierRow()"><mat-icon>add</mat-icon> Add Tier</button>
  </div>
</ng-template>

<!-- Notification step content -->
<ng-template #notificationStep>
  <h2>Configure notifications per tier</h2>
  <div *ngFor="let tn of tierNotifications" class="notification-tier-row">
    <mat-chip-list class="tier-card-chip" aria-label="Tier">
      <mat-chip [color]="tn.tierColor" selected>{{ tn.tierLabel }}</mat-chip>
    </mat-chip-list>
    <mat-slide-toggle [(ngModel)]="tn.alert_provider">Alert Provider</mat-slide-toggle>
    <mat-slide-toggle [(ngModel)]="tn.alert_patient">Alert Patient</mat-slide-toggle>
  </div>
</ng-template>
